import { defineStore } from "pinia";
import { AxiosError } from "axios";
import { organizationApi, OrganizationId, Organization } from "@/api/organizations";
import { useTeamsStore } from "./teams";
import { useUsersStore } from "./users";
import { ensureUsers, readCachedUser } from "@/queries/users";
import { ensureTeams } from "@/queries/teams";
import {
    ensureOrganizations,
    organizationTeamsQueryOptions,
    organizationUsersQueryOptions,
    readCachedOrganizations,
    readCachedOrganizationTeams,
    readCachedOrganizationUsers,
} from "@/queries/organizations";
import { ensureQueryData } from "@/queries/utils";
import { isDefined } from "@/utils";
import type { UserEditModel, UserId } from "@/api/users";

// Organization data lives in the query cache; getters read it reactively and
// mutations invalidate the affected keys.
export const useOrganizationsStore = defineStore("organizations", {
    getters: {
        organizations(): Map<OrganizationId, Organization> | undefined {
            const organizations = readCachedOrganizations();
            return organizations ? new Map(organizations.map((o) => [o.id, o])) : undefined;
        },
        get() {
            return (id: OrganizationId) => this.organizations?.get(id);
        },
        getTeams() {
            const teamStore = useTeamsStore();
            return (id: OrganizationId) =>
                (readCachedOrganizationTeams(id) ?? [])
                    .map((t) => teamStore.get(t))
                    .filter(isDefined);
        },
        getUsers() {
            return (id: OrganizationId) =>
                (readCachedOrganizationUsers(id) ?? [])
                    .map((u) => readCachedUser(u.id))
                    .filter(isDefined);
        },
        getIsExclusive() {
            return (id: OrganizationId, userId: UserId) =>
                readCachedOrganizationUsers(id)?.find((u) => u.id === userId)?.isExclusive ?? false;
        },
    },
    actions: {
        async createOrganization(name: string) {
            const organization = await organizationApi.create(name);
            await ensureOrganizations(true);

            return organization;
        },

        async deleteOrganization(id: OrganizationId) {
            try {
                await organizationApi.delete(id);
            } catch (ex) {
                if (ex instanceof AxiosError) {
                    return ex.response?.data as string;
                }

                throw ex;
            }

            await ensureOrganizations(true);

            return;
        },

        async load(force = false) {
            await this.loadOrganizations(force);
        },
        async loadOrganizations(force = false) {
            await ensureOrganizations(force);
        },

        async loadOrganization(id: OrganizationId, force = false) {
            if (force || !this.organizations?.has(id)) {
                await ensureOrganizations(true);
            }

            await this.loadOrganizationTeams(id, force);
            await this.loadOrganizationUsers(id, force);
        },

        async loadOrganizationTeams(id: OrganizationId, force = false) {
            await ensureTeams(force);
            await ensureQueryData(organizationTeamsQueryOptions(id), force);
        },

        async createTeam(organization: OrganizationId, name: string) {
            const team = await organizationApi.createTeam(organization, name);
            await ensureQueryData(organizationTeamsQueryOptions(organization), true);
            await ensureTeams(true);

            return team;
        },

        async loadOrganizationUsers(id: OrganizationId, force = false) {
            await ensureUsers();
            await ensureQueryData(organizationUsersQueryOptions(id), force);
        },

        async addUser(organization: OrganizationId, email: string | null, userName: string | null) {
            await organizationApi.addUser(organization, email, userName);
            await ensureQueryData(organizationUsersQueryOptions(organization), true);
        },

        async removeUser(organization: OrganizationId, user: UserId) {
            await organizationApi.removeUser(organization, user);
            await ensureQueryData(organizationUsersQueryOptions(organization), true);
        },

        async editUser(organization: OrganizationId, user: UserId, model: UserEditModel) {
            const userRes = await organizationApi.editOrganizationUser(organization, user, model);

            useUsersStore().modifyUser(userRes);
            await ensureQueryData(organizationUsersQueryOptions(organization), true);
        },
    },
});
